import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Snackbar,
  TextareaAutosize,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Code as CodeIcon,
  Visibility as VisibilityIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import mermaid from 'mermaid';
import MermaidEditor from './MermaidEditor';
import ConfirmationDialog from '../common/ConfirmationDialog';
import MarkdownRenderer from '../common/MarkdownRenderer';

mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  flowchart: { useMaxWidth: true, htmlLabels: true, curve: 'basis' },
});

// Flowchart node shapes, ordered most-specific first so longer brackets match
// before shorter ones (e.g. `[[...]]` before `[...]`).
const SHAPE_PATTERNS = [
  { open: '[[', close: ']]' }, // subroutine
  { open: '[(', close: ')]' }, // cylinder
  { open: '([', close: '])' }, // stadium
  { open: '{{', close: '}}' }, // hexagon
  { open: '[/', close: '\\]' }, // trapezoid
  { open: '[\\', close: '/]' }, // trapezoid alt
  { open: '[/', close: '/]' }, // parallelogram
  { open: '[\\', close: '\\]' }, // parallelogram alt
  { open: '>', close: ']' }, // asymmetric
  { open: '((', close: '))' }, // circle
  { open: '{', close: '}' }, // diamond
  { open: '(', close: ')' }, // rounded
  { open: '[', close: ']' }, // rectangle
];

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Encode user input for safe insertion as a quoted mermaid label. Mermaid
// renders these HTML entities as the literal characters.
const escapeForLabel = (s) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\|/g, '&#124;');

const decodeHtmlEntities = (s) => {
  if (!s) return '';
  return s
    .replace(/&quot;/g, '"')
    .replace(/&#124;/g, '|')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
};

// Locate a node's bracketed definition in the source. Returns the matched
// shape, current label, and exact slice indices for replacement.
const findNodeInSource = (code, nodeId) => {
  const idEsc = escapeRegex(nodeId);
  for (const { open, close } of SHAPE_PATTERNS) {
    const openEsc = escapeRegex(open);
    const closeEsc = escapeRegex(close);
    const re = new RegExp(
      `(^|[^A-Za-z0-9_-])${idEsc}\\s*${openEsc}\\s*"?([\\s\\S]*?)"?\\s*${closeEsc}`
    );
    const match = code.match(re);
    if (match) {
      // match.index points at the leading boundary char (or 0). Step past it.
      const lead = match[1] || '';
      const fullStart = match.index + lead.length;
      const fullLen = match[0].length - lead.length;
      return {
        label: match[2],
        openBracket: open,
        closeBracket: close,
        fullStart,
        fullLen,
      };
    }
  }
  return null;
};

const replaceNodeLabel = (code, nodeId, newText) => {
  const found = findNodeInSource(code, nodeId);
  if (!found) return null;
  const safe = escapeForLabel(newText);
  const replacement = `${nodeId}${found.openBracket}"${safe}"${found.closeBracket}`;
  return code.slice(0, found.fullStart) + replacement + code.slice(found.fullStart + found.fullLen);
};

// Mermaid renders `g.node` with `id="flowchart-{nodeId}-{counter}"`. Some
// versions emit a `data-id` attribute too. We try both.
const extractNodeId = (nodeEl) => {
  const m = (nodeEl.id || '').match(/^flowchart-(.+?)-\d+$/);
  if (m) return m[1];
  const dataId = nodeEl.getAttribute('data-id');
  if (dataId) return dataId;
  return null;
};

/**
 * Renders markdown like the accordion body. Click anywhere on it to switch
 * to a multiline textarea. Blur or press Escape to commit/discard.
 */
const EditableMarkdownBlock = ({ value, onChange, placeholder }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  // Sync draft when external value changes (e.g. on initial mount or
  // toggling between visual ↔ advanced views).
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== value) onChange(draft);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  if (editing) {
    return (
      <TextField
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        autoFocus
        multiline
        fullWidth
        minRows={3}
        maxRows={12}
        variant="outlined"
        size="small"
        placeholder={placeholder}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
            color: 'var(--color-blue, #475969)',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#475969', borderWidth: '1px' },
          },
          '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #4759694d', borderRadius: '4px' },
          '& .MuiInputBase-input': { fontSize: '14px' },
        }}
      />
    );
  }

  return (
    <Box
      onClick={() => setEditing(true)}
      sx={{
        cursor: 'text',
        p: 1.5,
        border: '1px dashed transparent',
        borderRadius: '4px',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': {
          borderColor: '#4759694d',
          backgroundColor: 'rgba(71, 89, 105, 0.02)',
        },
        minHeight: '36px',
      }}
    >
      {value ? (
        <Box
          sx={{
            fontSize: '14px',
            color: 'text.primary',
            '& p': { mb: 0.5 },
            '& ul, & ol': { mt: 0, mb: 0.5, pl: 2 },
            '& li': { mb: 0.25 },
            '& h1, & h2, & h3, & h4': { mt: 0.5, mb: 0.5 },
          }}
        >
          <MarkdownRenderer content={value} />
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          {placeholder || 'Click to add content'}
        </Typography>
      )}
    </Box>
  );
};

const ProcessFlowVisualEditor = ({
  initialCode = '',
  initialTitle = '',
  initialDescriptionBefore = '',
  initialDescriptionAfter = '',
  onSave,
  onCancel,
  height = '70vh',
}) => {
  const [mermaidCode, setMermaidCode] = useState(initialCode);
  const [diagramTitle, setDiagramTitle] = useState(initialTitle);
  const [descriptionBefore, setDescriptionBefore] = useState(initialDescriptionBefore);
  const [descriptionAfter, setDescriptionAfter] = useState(initialDescriptionAfter);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [editingNode, setEditingNode] = useState(null); // { id, x, y }
  const [editText, setEditText] = useState('');
  const [renderError, setRenderError] = useState(null);
  const [toast, setToast] = useState(null); // { message }
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const containerRef = useRef(null);
  const lastRenderIdRef = useRef('');

  const isDirty =
    mermaidCode !== initialCode ||
    diagramTitle !== initialTitle ||
    descriptionBefore !== initialDescriptionBefore ||
    descriptionAfter !== initialDescriptionAfter;

  const showToast = useCallback((message) => {
    setToast({ message });
  }, []);

  // Render mermaid into the container and wire click handlers.
  // The diagram is visible in both modes (visual-only and side-by-side with
  // code editor), so we don't early-return on showAdvanced anymore.
  const renderMermaid = useCallback(async () => {
    if (!mermaidCode.trim() || !containerRef.current) return;
    try {
      setRenderError(null);
      const renderId = `pfve-${Date.now()}`;
      lastRenderIdRef.current = renderId;
      const { svg } = await mermaid.render(renderId, mermaidCode);
      // Ignore stale renders if state changed while await was pending.
      if (lastRenderIdRef.current !== renderId) return;
      if (!containerRef.current) return;
      containerRef.current.innerHTML = svg;
      attachClickHandlers();
      // Mermaid leaks temp DOM nodes outside our container — clean them up.
      document.querySelectorAll(`[id^="d${renderId}"]`).forEach((el) => el.remove());
    } catch (err) {
      console.warn('ProcessFlowVisualEditor render error:', err);
      setRenderError(err?.message || 'Failed to render diagram');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mermaidCode]);

  // Re-render with debounce when code changes.
  useEffect(() => {
    const timer = setTimeout(() => renderMermaid(), 200);
    return () => clearTimeout(timer);
  }, [renderMermaid]);

  // Also re-render when the advanced pane is toggled — the diagram column
  // resizes (100% ↔ 50%) so the SVG should re-fit.
  useEffect(() => {
    const timer = setTimeout(() => renderMermaid(), 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvanced]);

  const attachClickHandlers = () => {
    if (!containerRef.current) return;
    const nodes = containerRef.current.querySelectorAll('g.node');
    nodes.forEach((nodeEl) => {
      const nodeId = extractNodeId(nodeEl);
      if (!nodeId) return;
      nodeEl.style.cursor = 'pointer';
      nodeEl.addEventListener('click', (e) => {
        e.stopPropagation();
        handleNodeClick(nodeEl, nodeId);
      });
    });
  };

  const handleNodeClick = (nodeEl, nodeId) => {
    const found = findNodeInSource(mermaidCode, nodeId);
    if (!found) {
      showToast("This shape can't be edited visually — use Show advanced.");
      return;
    }
    // Position the popover relative to the diagram column (the popover's
    // offsetParent — it has position: relative). Both rects are in viewport
    // coordinates and already reflect the container's internal scroll, so
    // we DON'T add scrollTop/scrollLeft (that double-counted the scroll
    // and put the popover in empty space below the visible node).
    const containerRect = containerRef.current.getBoundingClientRect();
    const nodeRect = nodeEl.getBoundingClientRect();
    setEditingNode({
      id: nodeId,
      x: nodeRect.left - containerRect.left + nodeRect.width / 2,
      y: nodeRect.bottom - containerRect.top + 8,
    });
    setEditText(decodeHtmlEntities(found.label));
  };

  const handleApplyEdit = () => {
    if (!editingNode) return;
    const trimmed = editText.trim();
    if (!trimmed) return;
    const newCode = replaceNodeLabel(mermaidCode, editingNode.id, trimmed);
    if (newCode === null) {
      showToast("Couldn't locate this node — use Show advanced.");
      setEditingNode(null);
      return;
    }
    setMermaidCode(newCode);
    setEditingNode(null);
    setEditText('');
  };

  const handleCancelEdit = () => {
    setEditingNode(null);
    setEditText('');
  };

  const handlePopoverKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  const handleCancelClick = () => {
    if (isDirty) {
      setConfirmCancel(true);
    } else if (onCancel) {
      onCancel();
    }
  };

  const handleConfirmDiscard = () => {
    setConfirmCancel(false);
    if (onCancel) onCancel();
  };

  const handleSave = async () => {
    if (!onSave || isSaving) return;
    setIsSaving(true);
    try {
      await onSave(mermaidCode, diagramTitle, descriptionBefore, descriptionAfter);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdvancedCodeChange = (newCode) => {
    setMermaidCode(newCode);
  };

  return (
    // Outer: grows to fit content. minHeight keeps it filling the dialog when
    // the diagram is small. The DialogContent wrapper handles the outer scroll
    // when content exceeds the dialog viewport — no nested scrollbars.
    <Box sx={{ minHeight: height, display: 'flex', flexDirection: 'column', px: 4, py: 1, position: 'relative' }}>
      {/* Title field */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ mb: 1, fontWeight: 700 }}>
          Diagram Title
        </Typography>
        <TextField
          value={diagramTitle}
          onChange={(e) => setDiagramTitle(e.target.value)}
          fullWidth
          placeholder="Enter diagram title"
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '4px',
              color: 'var(--color-blue, #475969)',
              height: '45px',
              transition: 'all 0.2s',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#475969',
                borderWidth: '1px',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#475969',
                borderWidth: '1px',
              }
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#4759694d',
              border: '1px solid #4759694d',
              borderRadius: '4px',
            },
            '& .MuiInputBase-input': {
              fontSize: '14px',
              padding: '12px 14px',
              color: 'var(--color-blue, #475969)',
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#a2abb3',
              opacity: 0.7,
              fontSize: '14px',
            },
          }}
        />
      </Box>

      {/* Description above diagram — click to edit */}
      <Box sx={{ mb: 1.5 }}>
        <EditableMarkdownBlock
          value={descriptionBefore}
          onChange={setDescriptionBefore}
          placeholder="Click to add description above the diagram (Flow Name, Primary Actor, intro paragraph…)"
        />
      </Box>

      {/* Toolbar: hint + advanced toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          💡 Click any node to rename it
        </Typography>
        <Button
          size="small"
          startIcon={showAdvanced ? <VisibilityIcon /> : <CodeIcon />}
          onClick={() => setShowAdvanced((v) => !v)}
          sx={{ textTransform: 'none' }}
        >
          {showAdvanced ? 'Hide code editor' : 'Show advanced (code editor)'}
        </Button>
      </Box>

      {/* Render area: diagram always visible, code editor appears beside it
          when advanced is on (50/50 split). The diagram has a guaranteed
          minimum height so descBefore/descAfter can't squeeze it; if the
          rendered SVG is taller than maxHeight, it scrolls inside its own
          container. */}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: showAdvanced ? 2 : 0 }}>
        {/* Diagram column (always rendered) */}
        <Box sx={{ flex: showAdvanced ? '1 1 50%' : '1 1 100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
          <Box
            ref={containerRef}
            sx={{
              minHeight: 480,
              maxHeight: '70vh',
              overflow: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              p: 2,
              backgroundColor: '#fafafa',
              '& svg': { display: 'block', margin: '0 auto', maxWidth: '100%', height: 'auto' },
              '& g.node': { transition: 'opacity 0.15s' },
              '& g.node:hover': { opacity: 0.85 },
            }}
          />

        {/* Inline edit popover */}
        {editingNode && (
          <Box
            sx={{
              position: 'absolute',
              top: editingNode.y,
              left: editingNode.x,
              transform: 'translateX(-50%)',
              backgroundColor: '#fff',
              border: '1px solid #475969',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              p: 1.5,
              zIndex: 10,
              minWidth: 280,
              maxWidth: 480,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
              Edit label
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <TextareaAutosize
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={handlePopoverKeyDown}
                size="small"
                fullWidth
                autoFocus
                inputProps={{ 'aria-label': 'Node label' }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    color: 'var(--color-blue, #475969)',
                    height: '45px',
                    transition: 'all 0.2s',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    }
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4759694d',
                    border: '1px solid #4759694d',
                    borderRadius: '4px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    padding: '12px 14px',
                    color: 'var(--color-blue, #475969)',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#a2abb3',
                    opacity: 0.7,
                    fontSize: '14px',
                  },
                }}
              />
              <Tooltip title="Apply (Enter)">
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={handleApplyEdit}
                    disabled={!editText.trim()}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Cancel (Esc)">
                <IconButton size="small" color="error" onClick={handleCancelEdit}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* Render error banner */}
        {renderError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {renderError}
          </Alert>
        )}
        </Box>

        {/* Code editor column (advanced mode only) — match the diagram's
            min-height so the two columns visually align in side-by-side mode. */}
        {showAdvanced && (
          <Box sx={{ flex: '1 1 50%', minHeight: 480, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
            <MermaidEditor
              initialCode={mermaidCode}
              initialTitle={diagramTitle}
              showToolbar={false}
              showCodeActions={false}
              showTitleField={false}
              onCodeChange={handleAdvancedCodeChange}
              height="100%"
            />
          </Box>
        )}
      </Box>

      {/* Description below diagram — click to edit */}
      <Box sx={{ mt: 1.5 }}>
        <EditableMarkdownBlock
          value={descriptionAfter}
          onChange={setDescriptionAfter}
          placeholder="Click to add Key Steps, notes, or links below the diagram"
        />
      </Box>

      {/* Bottom action bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          pt: 2,
          mt: 1,
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={handleCancelClick}
          disabled={isSaving}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={isSaving || !mermaidCode.trim() || !diagramTitle.trim()}
          sx={{ textTransform: 'none', minWidth: 140 }}
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>

      {/* Discard-changes confirmation */}
      <ConfirmationDialog
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        title="Discard your changes?"
        message="You have unsaved edits to this diagram. Are you sure you want to discard them?"
        confirmText="Discard"
        cancelText="Keep editing"
        confirmColor="error"
        onConfirm={handleConfirmDiscard}
      />

      {/* Unsupported-shape / parse-miss toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProcessFlowVisualEditor;
